import { apiConfig } from '@config/api'
import * as Sentry from '@sentry/react'
import type { IAxiosError } from '@shared/dtos'
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import { format } from 'date-fns'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { notifyPlatformHttpAlert } from '@/api/platform-alerts/notify-platform-http-alert'
import { env } from '@/env'
import { getRequiredIdToken } from '@/lib/get-authenticated-user'
// import { logger } from '@/lib/sentry'
import { captureApiError } from '@/shared/utils/sentry-custom-error'
import { sleep } from '@/shared/utils/sleep'

import { CacheAdapter } from '../cache'

function trySendPlatformAlert(
  requestUrl: string | undefined,
  codigoErro: string,
  mensagemErro: string,
  method: string,
  requestBody?: unknown,
  requestSearchParams?: unknown,
): void {
  const userData = CacheAdapter.get('userData')

  notifyPlatformHttpAlert({
    codigoErro,
    mensagemErro,
    method,
    requestUrl,
    requestBody,
    requestSearchParams,
    usuarioEmail: userData?.attributes?.email ?? '',
    usuarioNome: userData?.attributes?.name ?? '',
  })
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: `${apiConfig.baseUrl}`,
  headers: {
    'Content-Type': 'application/json; charset=utf-8;',
    Accept: 'application/json; charset=utf-8',
  },
})

const AxiosInterceptor: any = ({ children }: any) => {
  useEffect(() => {
    const amplifyInterceptor = async <T = any>(
      config: AxiosRequestConfig<T>,
    ): Promise<AxiosRequestConfig<T>> => {
      if (env.VITE_ENABLE_DELAY) {
        await sleep(1000)
      }

      try {
        const user = CacheAdapter.get('userData')

        Sentry.setUser({
          email: user?.attributes.email,
          username: user?.attributes.name,
        })
      } catch (e) {
        console.log('Error getting Amplify session:', e)

        Sentry.addBreadcrumb({
          category: 'logout',
          message: 'User logged out',
          level: 'info',
        })
      }

      // Log request to Sentry
      Sentry.addBreadcrumb({
        category: 'B2B-http',
        message: `Request: ${config.method?.toUpperCase()} ${config.url}`,
        level: 'info',
        data: {
          url: config.url,
          method: config.method,
          headers: JSON.stringify(config.headers, null, 2),
        },
      })
      // logger.debug(
      //   logger.fmt`HTTP request ${String(config.method).toUpperCase()} ${String(config.url)}`,
      // )

      return config
    }

    const getErrorResponseHandler = async (
      error: IAxiosError,
    ): Promise<void> => {
      const axiosError = error as IAxiosError & { message?: string }

      if (axiosError.response) {
        const { status, data } = axiosError.response

        let message = ''

        if (data.error) {
          message = data.error?.message ? data.error.message : ''

          message += data.error?.detail ? ` - ${data.error.detail}` : ''
        } else {
          Sentry.setTag('errorUnknown', JSON.stringify(data))

          message = 'Aviso, não foi possível processar a solicitação'
        }

        // E00-051 (duplicate booking attempt) has its own dedicated toast
        // shown by the booking form; skip the generic one to avoid duplicates
        const hasDedicatedToast = message.includes('E00-051')

        if (!hasDedicatedToast) {
          if (status >= 400 && status < 500) {
            toast.warning(
              `${message} - ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
              {
                className: 'text-warning',
                dismissible: true,
                duration: 5000,
              },
            )
          } else if (status >= 500) {
            toast.error(
              `${message} - ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
              {
                className: 'text-error',
                dismissible: true,
                duration: 5000,
              },
            )
          } else {
            toast.info(
              `${message} - ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
              {
                className: 'text-info',
                dismissible: true,
                duration: 5000,
              },
            )
          }
        }

        if (data?.error) {
          if (data.error.message) {
            Sentry.setTag('errorMessage', data.error.message)
          } else {
            Sentry.setTag('errorWithNoMessage', JSON.stringify(data.error))
          }
        }

        // Log error to Sentry
        Sentry.addBreadcrumb({
          category: 'B2B-http',
          message: `Response Error: ${status}`,
          level: 'error',
          data: {
            url: axiosError.config.url,
            method: axiosError.config.method,
            status,
            error: JSON.stringify(data, null, 2),
          },
        })
        // logger.error('HTTP response error', {
        //   url: axiosError.config.url,
        //   method: axiosError.config.method,
        //   status,
        // })

        try {
          const userData = CacheAdapter.get('userData')
          const user = userData?.attributes
            ? {
                id: userData.attributes.sub,
                username: userData.attributes.name,
                email: userData.attributes.email,
              }
            : undefined

          captureApiError(
            axiosError.config.url || 'unknown',
            axiosError.config.method?.toUpperCase() || 'UNKNOWN',
            status,
            message,
            user,
            {
              requestData: axiosError.config.data,
              responseData: data,
              timestamp: new Date().toISOString(),
              userAgent: navigator.userAgent,
            },
          )
        } catch (sentryError) {
          console.warn('Sentry logging failed:', sentryError)
        }
        const codigoErro = data?.error?.qualifiedErrCode ?? `HTTP-${status}`

        trySendPlatformAlert(
          axiosError.config?.url,
          codigoErro,
          message,
          axiosError.config?.method ?? 'UNKNOWN',
          axiosError.config?.data,
          (axiosError.config as any)?.params,
        )
      } else {
        trySendPlatformAlert(
          axiosError.config?.url,
          'NETWORK',
          axiosError.message || 'Erro de rede ou desconhecido',
          axiosError.config?.method ?? 'UNKNOWN',
          axiosError.config?.data,
          (axiosError.config as any)?.params,
        )

        // Log error to Sentry
        Sentry.addBreadcrumb({
          category: 'B2B-http',
          message: 'Network or unknown error',
          level: 'error',
          data: {
            error: JSON.stringify(axiosError, null, 2),
          },
        })
        // logger.error('HTTP network/unknown error')
      }

      Sentry.captureException(axiosError)

      await Promise.reject(axiosError)
    }

    // @ts-expect-error
    axiosInstance.interceptors.request.use(amplifyInterceptor)

    const interceptor = axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response to Sentry
        Sentry.addBreadcrumb({
          category: 'B2B-http',
          message: `Response: ${response.status} ${response.config.url}`,
          level: 'info',
          data: {
            url: response.config.url,
            method: response.config.method,
            status: response.status,
            response: JSON.stringify(response, null, 2),
          },
        })
        // logger.info(
        //   logger.fmt`HTTP response ${response.status} ${String(
        //     response.config.url,
        //   )}`,
        // )

        return response
      },
      getErrorResponseHandler,
    )

    return () => {
      axiosInstance.interceptors.response.eject(interceptor)
    }
  }, [])

  return children
}

axiosInstance.interceptors.request.use(async config => {
  const token = await getRequiredIdToken()

  const language = localStorage.getItem('i18nextLng') || 'pt-BR'

  config.headers.Authorization = token
  config.headers['Accept-Language'] = language

  return config
})

async function withHttpSpan<T>(
  name: string,
  method: string,
  url: string,
  fn: () => Promise<AxiosResponse<T>>,
): Promise<AxiosResponse<T>> {
  return await Sentry.startSpan(
    {
      name,
      op: 'http.client',
      attributes: {
        'http.method': method,
        'http.url.path': url,
      },
    },
    async () => {
      const response = await fn()
      const active = Sentry.getActiveSpan()
      active?.setAttribute('http.status_code', response.status)
      return response
    },
  )
}

const get = async <T = any>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  const echoToken = uuidv4()
  const updatedUrl = `${url}?echoToken=${echoToken}`

  return await withHttpSpan<T>(`GET ${url}`, 'GET', url, async () => {
    const response: AxiosResponse<T> = await axiosInstance.get<T>(
      updatedUrl,
      config,
    )
    return response
  })
}

const post = async (
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  const echoToken = uuidv4()
  const updatedData = { echoToken, ...data }

  return await withHttpSpan(`POST ${url}`, 'POST', url, async () => {
    const response: AxiosResponse = await axiosInstance.post(
      url,
      updatedData,
      config,
    )
    return response
  })
}

const put = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  const echoToken = uuidv4()
  const updatedData = { echoToken, ...data }

  return await withHttpSpan<T>(`PUT ${url}`, 'PUT', url, async () => {
    const response: AxiosResponse<T> = await axiosInstance.put<T>(
      url,
      updatedData,
      config,
    )
    return response
  })
}

const patch = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return await withHttpSpan<T>(`PATCH ${url}`, 'PATCH', url, async () => {
    const response: AxiosResponse<T> = await axiosInstance.patch<T>(
      url,
      data,
      config,
    )

    return response
  })
}

const del = async <T = any>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return await withHttpSpan<T>(`DELETE ${url}`, 'DELETE', url, async () => {
    const response: AxiosResponse<T> = await axiosInstance.delete<T>(
      url,
      config,
    )
    return response
  })
}

export const httpClient = {
  get,
  post,
  put,
  patch,
  del,
  AxiosInterceptor,
}
